import React, { useCallback } from "react";
import { withTranslation } from "react-i18next";
import { compose } from "recompose";
import { connect } from "react-redux";
import { Icon, Tooltip, Modal, Spin } from "antd";
import * as R from "ramda";

import { isLoading } from "../../../utils/actions";
import {
  fetchUserCalendars,
  FETCH_USER_CALENDARS,
  selectUserCalendar,
  SELECT_USER_CALENDAR
} from "../../../store/actions/user";
import Button from "../../../components/Button";

import SelectCalendarModal from "./SelectCalendarModal";

import styled from "styled-components/macro";

const redirectUri = encodeURI(process.env.REACT_APP_CRONOFY_REDIRECT_URI);
const clientId = process.env.REACT_APP_CRONOFY_CLIENT_ID;

const Container = styled.div`
  display: flex;
  align-items: center;
  margin: 0 24px;
  width: 220px;
  .spinner-wrapper {
    width: 100%;
  }
`;

const SelectedCalendar = styled.div`
  .title {
    margin-bottom: 12px;
  }

  .calendar-name {
    font-weight: bold;
    text-overflow: ellipsis;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    display: block;
  }
`;

const Calendar = ({
  t,
  history,
  agenda,
  fetchUserCalendars,
  fetchingCalendars,
  selectUserCalendar,
  updatingCalendar
}) => {
  const [showSelectCalendarModal, setShowSelectCalendarModal] = React.useState(
    true
  );
  const [showNoCalendarWarning, setShowNoCalendarWarning] = React.useState(
    false
  );

  const url = `https://app.cronofy.com/oauth/v2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=read_write`;
  const selected = R.pipe(
    R.propOr([], "calendars"),
    R.find(R.prop("selected"))
  )(agenda);

  React.useEffect(
    () => {
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");

      if (!code || R.includes("companyId", url.search)) return;

      // Remove query from url to prevent reseting agenda values
      // if user refresh the page
      history.replace(history.location.pathname);
      setShowSelectCalendarModal(true);
      fetchUserCalendars({
        agenda: {
          code,
          redirect_uri: redirectUri
        }
      });
    },
    // eslint-disable-next-line
    []
  );

  const onDeleteCalendar = useCallback(() => {
    selectUserCalendar({ agenda: null });
  }, [selectUserCalendar]);

  // If there are no calendars remove agenda data from user
  // so they can connect to calendar service again and show warning
  const isCalendarListEmpty = agenda.calendars && agenda.calendars.length === 0;
  React.useEffect(() => {
    if (isCalendarListEmpty) {
      setShowSelectCalendarModal(false);
      setShowNoCalendarWarning(true);
      onDeleteCalendar();
    }
  }, [isCalendarListEmpty, onDeleteCalendar]);

  const onSelectCalendar = calendarId => {
    const calendars = R.map(
      c => ({ ...c, selected: c.calendar_id === calendarId }),
      agenda.calendars || []
    );
    selectUserCalendar({ agenda: { ...agenda, calendars } });
  };

  return (
    <Container data-test="connect-calendar">
      <Spin
        size="small"
        spinning={fetchingCalendars}
        wrapperClassName="spinner-wrapper"
      >
        <SelectedCalendar>
          <div className="title">
            <div>
              <span
                data-test={
                  selected
                    ? "calendar-connected-title"
                    : "calendar-not-connected-title"
                }
              >
                {selected
                  ? t("user:calendar.calendarConnected")
                  : t("user:calendar.calendarNotConnected")}
              </span>
              <Tooltip title={t("user:calendar.calendarTooltip")}>
                <Icon
                  style={{ marginLeft: 8 }}
                  theme="filled"
                  type="question-circle"
                />
              </Tooltip>
            </div>
            {selected && (
              <span
                data-test="selected-calendar"
                title={selected.calendar_name}
                className="calendar-name"
              >
                {selected.calendar_name}
              </span>
            )}
          </div>
          {selected ? (
            <Button
              type="alert"
              size="sm"
              loading={updatingCalendar}
              onClick={() => {
                Modal.confirm({
                  title: t("global:areYouSure"),
                  content: t("user:calendar.deleteCalendarConfirmation"),
                  okText: t("global:delete"),
                  onOk: onDeleteCalendar,
                  okType: "danger",
                  cancelText: t("global:cancel")
                });
              }}
            >
              {t("user:calendar.deleteCalendar")}
            </Button>
          ) : agenda && agenda.calendars ? (
            <Button
              icon="calendar"
              size="sm"
              onClick={() => {
                setShowSelectCalendarModal(true);
              }}
              loading={updatingCalendar}
            >
              {t("user:calendar.selectCalendar")}
            </Button>
          ) : (
            <a href={url}>
              <Button
                icon="calendar"
                size="sm"
                loading={updatingCalendar}
                data-test="connect-calendar-btn"
              >
                {t("user:calendar.connect")}
              </Button>
            </a>
          )}
        </SelectedCalendar>
      </Spin>
      {showSelectCalendarModal &&
        agenda.calendars &&
        agenda.calendars.length &&
        !selected && (
          <SelectCalendarModal
            t={t}
            calendars={agenda.calendars}
            onDeleteCalendar={onDeleteCalendar}
            onSelectCalendar={onSelectCalendar}
            fetchingCalendars={fetchingCalendars}
            updatingCalendar={updatingCalendar}
            onClose={() => setShowSelectCalendarModal(false)}
            setShowNoCalendarWarning={setShowNoCalendarWarning}
          />
        )}
      {showNoCalendarWarning && (
        <Modal
          visible={true}
          title={
            <div>
              <Icon
                theme="twoTone"
                twoToneColor="red"
                type="warning"
                style={{ marginRight: 8 }}
              />
              <span>{t("user:calendar.noCalendarsWarningTitle")}</span>
            </div>
          }
          footer={null}
          onCancel={() => {
            setShowNoCalendarWarning(false);
          }}
        >
          <p>{t("user:calendar.noCalendarsWarningDescription")}</p>
        </Modal>
      )}
    </Container>
  );
};

const enhancer = compose(
  withTranslation(["global", "user"]),
  connect(
    state => ({
      agenda: R.pathOr({}, ["user", "agenda"], state),
      fetchingCalendars: isLoading(FETCH_USER_CALENDARS, state),
      updatingCalendar: isLoading(SELECT_USER_CALENDAR, state)
    }),
    { fetchUserCalendars, selectUserCalendar }
  )
);

export default enhancer(Calendar);
